/**
 * Community Standards — the friendly, plain-English rules for the forum,
 * DMs, and live sessions. Linked from the forum header, DMs, footer, and
 * signup.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, ArrowLeft, Heart, Ban, Flag, MessageSquare, Camera } from 'lucide-react';

const Rule = ({ icon, title, children }) => (
  <div className="pro-card p-4">
    <div className="flex items-start gap-3">
      <div className="h-9 w-9 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <h3 className="font-heading text-base font-extrabold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground mt-1">{children}</p>
      </div>
    </div>
  </div>
);

const CommunityStandards = () => (
  <div className="min-h-screen bg-background">
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
      <Link to="/community" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to community
      </Link>

      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Community</div>
          <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground leading-tight" data-testid="community-standards-heading">
            Community Standards
          </h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-xl">
            We&rsquo;re here to learn from each other. These are the guardrails
            that keep the Academy welcoming for everyone &mdash; from first-day
            beginners to twenty-year pros.
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-3">
        <Rule icon={<Heart className="w-4 h-4" />} title="Be kind. Be curious.">
          Assume good intent. Disagree respectfully. Freeze-drying has a
          long learning curve &mdash; nobody arrives an expert.
        </Rule>
        <Rule icon={<Ban className="w-4 h-4" />} title="No harassment, hate, or slurs">
          Attacks on race, religion, gender, sexuality, disability, or
          identity get you a permanent ban. No exceptions.
        </Rule>
        <Rule icon={<Camera className="w-4 h-4" />} title="Keep photos family-friendly">
          Cooking, packaging, and product shots are perfect. Anything
          sexual, graphic, or self-harm-related is automatically screened
          out by our safety filter and will be removed.
        </Rule>
        <Rule icon={<MessageSquare className="w-4 h-4" />} title="Post honestly">
          Don&rsquo;t astroturf, don&rsquo;t spread misinformation about food
          safety, don&rsquo;t plagiarize. Cite your sources when you can.
        </Rule>
        <Rule icon={<Flag className="w-4 h-4" />} title="Report, don&rsquo;t retaliate">
          If someone crosses the line, use the &ldquo;Report to Moderator&rdquo; button on
          the post or reply. We review every report within 48 hours.
          Don&rsquo;t reply-troll or pile on &mdash; that gets both parties in trouble.
        </Rule>
      </div>

      <section className="mt-8">
        <h2 className="font-heading text-xl font-black text-foreground">How we enforce</h2>
        <p className="text-sm text-muted-foreground mt-2">
          Every post, reply, and Direct Message goes through an automated profanity
          filter. Uploaded photos are screened for explicit content by an
          image-safety model. First offenses usually get a warning; repeated
          or severe breaches get muted, suspended, or banned. Serious
          violations (illegal content, doxxing, threats) are actioned
          immediately.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          These standards sit alongside our{' '}
          <Link to="/terms" className="underline">Terms of Use</Link>. If
          you spot a mistake in enforcement, Direct Message an admin or use the Help
          Center &mdash; we listen.
        </p>
      </section>
    </div>
  </div>
);

export default CommunityStandards;
